import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import { updateSession } from '../../app/slices/sessionSlice';
import { addToast } from '../../app/slices/uiSlice';
import LoadingSpinner from '../../components/LoadingSpinner';

interface EditSessionFormProps {
  sessionId: string;
  onCancel: () => void;
  onSaved: (saved: any) => void;
}

export default function EditSessionForm({ sessionId, onCancel, onSaved }: EditSessionFormProps) {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { sessions, isLoading } = useSelector((state: RootState) => state.sessions);

  const existing = sessions.find(s => s.id === sessionId);

  const [startOdometer, setStartOdometer] = useState('');
  const [endOdometer, setEndOdometer] = useState('');
  const [totalKmWithPassenger, setKmWithPassenger] = useState('');
  const [grossEarnings, setGrossEarnings] = useState('');
  const [drivingDurationHours, setDrivingDurationHours] = useState('');
  const [drivingDurationMinutes, setDrivingDurationMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existing) {
      setStartOdometer(String(existing.startOdometer ?? ''));
      setEndOdometer(String(existing.endOdometer ?? ''));
      setKmWithPassenger(String(existing.totalKmWithPassenger ?? ''));
      setGrossEarnings(String(existing.grossEarnings ?? ''));

      // Set driving duration from existing session
      if (existing.drivingDurationMinutes) {
        const hours = Math.floor(existing.drivingDurationMinutes / 60);
        const minutes = existing.drivingDurationMinutes % 60;
        setDrivingDurationHours(String(hours));
        setDrivingDurationMinutes(String(minutes));
      }

      setDate(new Date(existing.startTime.toDate()).toISOString().split('T')[0]);
      setNotes((existing as any).notes || '');
    }
  }, [existing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid || !existing) return;

    const hours = parseInt(drivingDurationHours || '0');
    const minutes = parseInt(drivingDurationMinutes || '0');
    const totalDrivingMinutes = hours * 60 + minutes;

    const updates: any = {
      startOdometer: parseFloat(startOdometer) || 0,
      endOdometer: parseFloat(endOdometer) || 0,
      totalKmWithPassenger: parseFloat(totalKmWithPassenger) || 0,
      grossEarnings: parseFloat(grossEarnings) || 0,
      drivingDurationMinutes: totalDrivingMinutes > 0 ? totalDrivingMinutes : undefined,
      notes,
    };
    if (date) {
      updates.startTime = existing.startTime as any; // keep same unless we implement date change fully
    }

    setIsSubmitting(true);
    try {
      const action: any = await dispatch(
        updateSession({
          userId: user.uid,
          sessionId,
          updates,
          provinceCode: user.province,
        })
      );
      const saved = action.payload;
      if (saved) onSaved(saved);
      else addToast({ message: 'Failed to update session', type: 'error' });
    } catch {
      /* handled */
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Start Odometer (km)</label>
          <input
            type="number"
            className="input"
            value={startOdometer}
            onChange={e => setStartOdometer(e.target.value)}
          />
        </div>
        <div>
          <label className="label">End Odometer (km)</label>
          <input
            type="number"
            className="input"
            value={endOdometer}
            onChange={e => setEndOdometer(e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">KM with Passenger</label>
          <input
            type="number"
            className="input"
            value={totalKmWithPassenger}
            onChange={e => setKmWithPassenger(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Gross Earnings (CAD)</label>
          <input
            type="text"
            inputMode="decimal"
            className="input"
            value={grossEarnings}
            onChange={e => setGrossEarnings(e.target.value.replace(',', '.'))}
          />
        </div>
      </div>
      <div>
        <label className="label">Driving Duration</label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="number"
              placeholder="Hours"
              min="0"
              max="24"
              value={drivingDurationHours}
              onChange={e => setDrivingDurationHours(e.target.value)}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Hours</p>
          </div>
          <div className="flex-1">
            <input
              type="number"
              placeholder="Minutes"
              min="0"
              max="59"
              value={drivingDurationMinutes}
              onChange={e => setDrivingDurationMinutes(e.target.value)}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Minutes</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Uber shows the total time you drove during the session
        </p>
      </div>
      <div>
        <label className="label">Date</label>
        <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <div>
        <label className="label">Notes</label>
        <textarea
          className="input"
          rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
        >
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting || isLoading}>
          {isSubmitting || isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" /> Saving...
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </form>
  );
}
